import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import { useTranslation } from "react-i18next";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  Circle,
  Polyline,
} from '@react-google-maps/api';
import { Loader2, MapPin, Navigation, Car, Clock, Footprints, Bike, Utensils } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import {
  GOOGLE_MAPS_LIBRARIES,
  loadGoogleMapsKey,
  getCachedGoogleMapsKey,
  useBeraMapsLoader,
} from '@/lib/googleMapsLoader';
import {
  DEFAULT_MAP_OPTIONS,
  getBeraMarkerIcon,
  getUserDotIcon,
} from '@/lib/googleMapStyles';
import { useMapPOIs, POI_TYPES, type POI } from '@/hooks/useMapPOIs';
import { usePOIDistances, type POIWithDistance } from '@/hooks/usePOIDistances';

interface PropertyMapOptimizedProps {
  latitude: number | null;
  longitude: number | null;
  address: string;
  city: string;
  province: string;
  country: string | null;
}

interface TravelInfo {
  distance: string;
  duration: string;
  durationSeconds: number;
}

type TransportMode = 'driving' | 'walking' | 'bicycling';

const transportModes: { mode: TransportMode; labelKey: string; defaultLabel: string; icon: typeof Car }[] = [
  { mode: 'driving', labelKey: 'map.transport.driving', defaultLabel: 'Carro', icon: Car },
  { mode: 'walking', labelKey: 'map.transport.walking', defaultLabel: 'A pé', icon: Footprints },
  { mode: 'bicycling', labelKey: 'map.transport.bicycling', defaultLabel: 'Bicicleta', icon: Bike },
];


const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)} seg`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
};
const formatDistance = (m: number): string =>
  m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`;

// Decode an encoded polyline (Google format) into lat/lng pairs.
const decodePolyline = (str: string): { lat: number; lng: number }[] => {
  const points: { lat: number; lng: number }[] = [];
  let index = 0, lat = 0, lng = 0;
  while (index < str.length) {
    let b, shift = 0, result = 0;
    do {
      b = str.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = (result & 1) ? ~(result >> 1) : result >> 1;
    lat += dlat;
    shift = 0; result = 0;
    do {
      b = str.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = (result & 1) ? ~(result >> 1) : result >> 1;
    lng += dlng;
    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return points;
};

const getCachedUserCoords = (): { lat: number; lng: number } | null => {
  try {
    const rawSession = sessionStorage.getItem('user_location');
    if (rawSession) {
      const d = JSON.parse(rawSession);
      if (typeof d?.latitude === 'number' && typeof d?.longitude === 'number')
        return { lat: d.latitude, lng: d.longitude };
    }
    const rawLocal = localStorage.getItem('berastays_location_cache');
    if (!rawLocal) return null;
    const d = JSON.parse(rawLocal);
    if (typeof d?.lat === 'number' && typeof d?.lng === 'number') return { lat: d.lat, lng: d.lng };
  } catch {
    return null;
  }
  return null;
};

const containerStyle = { width: '100%', height: '100%' };

// Backwards-compatible warmup helper (replaces preloadMapboxToken across the codebase).
export const preloadMapboxToken = () => loadGoogleMapsKey();

const PropertyMapOptimized = memo(({ latitude, longitude, address, city, province, country }: PropertyMapOptimizedProps) => {
  const { t } = useTranslation();
  const [apiKey, setApiKey] = useState<string | null>(getCachedGoogleMapsKey());
  const [propertyCoords, setPropertyCoords] = useState<{ lat: number; lng: number } | null>(
    latitude && longitude ? { lat: latitude, lng: longitude } : null,
  );
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [travelInfo, setTravelInfo] = useState<TravelInfo | null>(null);
  const [transportMode, setTransportMode] = useState<TransportMode>('driving');
  const [showPOIs, setShowPOIs] = useState(true);
  const [sortBy, setSortBy] = useState<'time' | 'distance' | 'category'>('time');
  const [activeCategories, setActiveCategories] = useState<Set<string>>(
    new Set(Object.keys(POI_TYPES)),
  );
  const [routePath, setRoutePath] = useState<{ lat: number; lng: number }[]>([]);
  const [showInfo, setShowInfo] = useState(false);
  const [activePoi, setActivePoi] = useState<POIWithDistance | null>(null);
  const [isLoadingRoute, setIsLoadingRoute] = useState(false);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const rawPois = useMapPOIs({
    enabled: true,
    map: mapInstance,
    center: propertyCoords,
  });

  // Calculate local Haversine distances to either user location or property
  const pois = usePOIDistances(rawPois, userLocation || propertyCoords);

  useEffect(() => {
    if (apiKey) return;
    loadGoogleMapsKey().then((k) => k && setApiKey(k));
  }, [apiKey]);

  const { isLoaded, loadError } = useBeraMapsLoader(apiKey);
  const [isApproximate, setIsApproximate] = useState<boolean>(latitude == null || longitude == null);
  const geocodedRef = useRef(false);

  useEffect(() => {
    if (propertyCoords || geocodedRef.current) return;
    
    const applyFictionalFallback = () => {
      const base = { lat: -8.8390, lng: 13.2894 };
      const seedString = (address || "") + (city || "") + (province || "");
      let hash = 0;
      for (let i = 0; i < seedString.length; i++) {
        hash = Math.imul(31, hash) + seedString.charCodeAt(i) | 0;
      }
      const random1 = Math.abs(Math.sin(hash)) * 0.04;
      const random2 = Math.abs(Math.cos(hash)) * 0.04;
      setPropertyCoords({ lat: base.lat + (random1 - 0.02), lng: base.lng + (random2 - 0.02) });
      setIsApproximate(false); 
    };

    if (!address && !city) {
      applyFictionalFallback();
      return;
    }
    
    geocodedRef.current = true;
    (async () => {
      try {
        const query = [address, city, province, country || "Angola"].filter(Boolean).join(", ");
        const { data } = await supabase.functions.invoke("google-geocode", { body: { query } });
        if (data?.result?.lat && data?.result?.lng) {
          setPropertyCoords({ lat: data.result.lat, lng: data.result.lng });
          setIsApproximate(true);
        } else {
          applyFictionalFallback();
        }
      } catch {
        applyFictionalFallback();
      }
    })();
  }, [address, city, province, country, propertyCoords]);

  // User location from cache
  useEffect(() => {
    const c = getCachedUserCoords();
    if (c) setUserLocation(c);
  }, []);

  // Route fetch for Property -> User OR Property -> activePoi
  useEffect(() => {
    // If we have an active POI, fetch exact route to it from userLocation or propertyCoords
    const origin = userLocation || propertyCoords;
    const dest = activePoi ? activePoi.position : propertyCoords;

    if (!origin || !dest || (origin.lat === dest.lat && origin.lng === dest.lng)) return;
    
    setIsLoadingRoute(true);
    supabase.functions
      .invoke('google-directions', {
        body: {
          originLat: origin.lat,
          originLng: origin.lng,
          destLat: dest.lat,
          destLng: dest.lng,
          mode: transportMode,
        },
      })
      .then(({ data }) => {
        if (data?.durationSeconds != null) {
          setTravelInfo({
            duration: formatDuration(data.durationSeconds),
            distance: formatDistance(data.distanceMeters),
            durationSeconds: data.durationSeconds,
          });
          if (data.polyline) setRoutePath(decodePolyline(data.polyline));
        }
      })
      .finally(() => setIsLoadingRoute(false));
  }, [userLocation, propertyCoords, transportMode, activePoi]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMapInstance(map);
  }, []);


  const markerIcon = useMemo(() => (isLoaded ? getBeraMarkerIcon(44) : undefined), [isLoaded]);
  const userIcon = useMemo(() => (isLoaded ? getUserDotIcon() : undefined), [isLoaded]);

  const presentCategories = useMemo(
    () => [...new Set(pois.map((p) => p.category))],
    [pois],
  );
  const toggleCategory = (cat: string) =>
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) {
        next.delete(cat);
      } else {
        next.add(cat);
      }
      return next;
    });

  const visiblePois = pois.filter((p) => activeCategories.has(p.category));

  const sortedPois = useMemo(() => {
    return [...visiblePois].sort((a, b) => {
      if (sortBy === 'distance') return a.distanceMeters - b.distanceMeters;
      if (sortBy === 'time') return a.drivingDurationSeconds - b.drivingDurationSeconds;
      const catCompare = a.category.localeCompare(b.category);
      if (catCompare !== 0) return catCompare;
      return a.drivingDurationSeconds - b.drivingDurationSeconds;
    });
  }, [visiblePois, sortBy]);

  const CurrentIcon = transportModes.find((t) => t.mode === transportMode)?.icon || Car;

  if (loadError) {
    return (
      <div className="aspect-video rounded-2xl bg-muted flex items-center justify-center">
        <div className="text-center">
          <MapPin className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
          <p className="text-muted-foreground">{address}, {city}</p>
          <p className="text-sm text-muted-foreground mt-1">{province}, {country}</p>
        </div>
      </div>
    );
  }

  const ready = isLoaded && propertyCoords;

  return (
    <div className="space-y-3">
      <div className="relative aspect-video rounded-2xl overflow-hidden">
      {ready && isApproximate && (
        <div className="absolute top-3 left-3 right-3 z-10 bg-amber-50/95 border border-amber-200 rounded-xl px-3 py-2 text-[11px] text-amber-900 shadow-sm backdrop-blur-sm">
          {t('map.approximateLocation', { defaultValue: 'Localização aproximada · o proprietário ainda não fixou o pin exato. A zona apresentada pode não corresponder ao endereço real.' })}
        </div>
      )}
        {!ready && (
          <div className="absolute inset-0 bg-muted flex items-center justify-center z-10">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {ready && propertyCoords && (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={propertyCoords}
            zoom={14}
            options={DEFAULT_MAP_OPTIONS}
            onLoad={onMapLoad}
          >
            <Marker
              position={propertyCoords}
              icon={markerIcon}
              onClick={() => setShowInfo((v) => !v)}
              zIndex={999999}
            />


            {showInfo && (
              <InfoWindow
                position={propertyCoords}
                onCloseClick={() => setShowInfo(false)}
              >
                <div style={{ padding: '6px 10px', fontFamily: 'system-ui' }}>
                  <p style={{ fontWeight: 700, margin: 0, fontSize: 13 }}>{address}</p>
                  <p style={{ margin: '2px 0 0', color: '#888', fontSize: 12 }}>
                    {city}, {province}
                  </p>
                </div>
              </InfoWindow>
            )}

            <Circle
              center={propertyCoords}
              radius={250}
              options={{
                fillColor: 'hsl(12, 82%, 52%)',
                fillOpacity: 0.12,
                strokeColor: 'hsl(12, 82%, 52%)',
                strokeOpacity: 0.4,
                strokeWeight: 2,
                clickable: false,
              }}
            />

            {userLocation && <Marker position={userLocation} icon={userIcon} />}

            {routePath.length > 0 && (
              <Polyline
                path={routePath}
                options={{ strokeColor: '#4285F4', strokeOpacity: 0.85, strokeWeight: 4 }}
              />
            )}

            {showPOIs &&
              visiblePois.map((poi, idx) => (
                <Marker
                  key={`${poi.category}-${idx}`}
                  position={poi.position}
                  icon={poi.markerUrl ? { url: poi.markerUrl } : {
                    path: typeof window !== 'undefined' && window.google && window.google.maps ? window.google.maps.SymbolPath.CIRCLE : 0,
                    scale: 16,
                    fillColor: '#fff',
                    fillOpacity: 1,
                    strokeColor: poi.color,
                    strokeWeight: 2.5,
                  }}
                  label={poi.markerUrl ? undefined : { text: poi.icon, fontSize: '18px' }}
                  onClick={() => setActivePoi(poi)}
                  zIndex={idx + 1}
                />
              ))}

            {activePoi && (
              <InfoWindow position={activePoi.position} onCloseClick={() => setActivePoi(null)}>
                <div style={{ padding: '6px 12px', fontFamily: 'system-ui', minWidth: '140px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <div style={{ 
                      width: '28px', height: '28px', borderRadius: '50%', 
                      backgroundColor: activePoi.color, display: 'flex', 
                      alignItems: 'center', justifyContent: 'center' 
                    }}>
                      <span style={{ fontSize: 14, color: 'white', lineHeight: 1 }}>{activePoi.icon}</span>
                    </div>
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#333', lineHeight: 1.2 }}>{activePoi.name}</span>
                  </div>
                  <span
                    style={{
                      display: 'inline-block',
                      fontSize: '10px',
                      fontWeight: 600,
                      color: 'white',
                      background: activePoi.color,
                      padding: '2px 8px',
                      borderRadius: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    {POI_TYPES[activePoi.category]?.label}
                  </span>
                  <div style={{ marginTop: '8px', display: 'flex', gap: '4px', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#555' }}>
                      <span style={{ fontSize: '12px' }}>🚗</span> 
                      <span style={{ fontWeight: 600 }}>{activePoi.drivingDurationText}</span> • {activePoi.distanceText}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#555' }}>
                      <span style={{ fontSize: '12px' }}>🏍️</span> 
                      <span style={{ fontWeight: 600 }}>{activePoi.motorcycleDurationText}</span> • {activePoi.distanceText}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#555' }}>
                      <span style={{ fontSize: '12px' }}>🚲</span> 
                      <span style={{ fontWeight: 600 }}>{activePoi.bicyclingDurationText}</span> • {activePoi.distanceText}
                    </div>
                  </div>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        )}

        {ready && pois.length > 0 && (
          <div className="absolute top-4 right-14 z-10">
            <Button
              variant="outline"
              size="sm"
              className={`h-8 gap-1.5 text-xs shadow-lg backdrop-blur-sm border ${
                showPOIs
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90 border-primary'
                  : 'bg-background/90 text-foreground hover:bg-background border-border'
              }`}
              onClick={() => setShowPOIs(!showPOIs)}
            >
              <Utensils className="w-3.5 h-3.5" />
              {t('map.nearbyPlaces', { count: pois.length, defaultValue: '{{count}} locais próximos' })}
            </Button>
          </div>
        )}

        <div className="absolute bottom-4 left-4 bg-background/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-md text-sm">
          <p className="font-medium">{address}</p>
          <p className="text-muted-foreground text-xs">{city}, {province}</p>
        </div>
      </div>

      {/* POI category chips */}
      {showPOIs && presentCategories.length > 0 && (
        <div className="flex overflow-x-auto pb-2 -mx-2 px-2 gap-2 scrollbar-none" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {presentCategories.map((cat) => {
            const cfg = POI_TYPES[cat];
            const active = activeCategories.has(cat);
            return (
              <button
                key={cat}
                onClick={() => toggleCategory(cat)}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-all flex-shrink-0 font-medium ${
                  active
                    ? 'border-transparent text-white shadow-sm'
                    : 'border-border text-muted-foreground bg-background hover:bg-muted'
                }`}
                style={active ? { backgroundColor: cfg?.color } : undefined}
              >
                {cfg?.iconElement && React.cloneElement(cfg.iconElement, { size: 14 })}
                {cfg?.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Travel info */}
      {travelInfo && (
        <div className="flex items-center justify-between bg-muted/50 rounded-xl p-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
              {isLoadingRoute ? (
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
              ) : (
                <CurrentIcon className="w-4 h-4 text-primary" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5 text-primary" />
                <span className="font-semibold text-sm">{travelInfo.duration}</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Navigation className="w-3 h-3" />
                <span>{travelInfo.distance}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-1 bg-background rounded-lg p-1">
            {transportModes.map(({ mode, labelKey, defaultLabel, icon: Icon }) => (
              <button
                key={mode}
                aria-label={t(labelKey, { defaultValue: defaultLabel })}
                title={t(labelKey, { defaultValue: defaultLabel })}
                onClick={() => setTransportMode(mode)}
                className={`p-2 rounded-md transition-colors ${
                  transportMode === mode
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                <Icon className="w-4 h-4" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* POI List */}
      {showPOIs && sortedPois.length > 0 && (
        <div className="bg-white dark:bg-zinc-900 border border-border rounded-2xl overflow-hidden mt-4">
          <div className="p-4 border-b flex items-center justify-between bg-muted/30">
            <h3 className="font-semibold text-sm">{t('map.nearbyPlacesTitle', { count: sortedPois.length, defaultValue: 'Locais Próximos ({{count}})' })}</h3>
            <div className="flex gap-2 text-xs">
              <button 
                onClick={() => setSortBy('time')}
                className={`px-3 py-1.5 rounded-md transition-colors ${sortBy === 'time' ? 'bg-primary text-primary-foreground font-medium' : 'bg-background hover:bg-muted'}`}
              >
                {t('map.sort.time', { defaultValue: 'Tempo' })}
              </button>
              <button 
                onClick={() => setSortBy('distance')}
                className={`px-3 py-1.5 rounded-md transition-colors ${sortBy === 'distance' ? 'bg-primary text-primary-foreground font-medium' : 'bg-background hover:bg-muted'}`}
              >
                {t('map.sort.distance', { defaultValue: 'Distância' })}
              </button>
            </div>
          </div>
          <div className="max-h-[300px] overflow-y-auto divide-y divide-border/50">
            {sortedPois.map((poi, i) => (
              <div 
                key={`${poi.name}-${i}`}
                onClick={() => setActivePoi(poi)}
                className={`p-3 flex items-center gap-4 cursor-pointer hover:bg-muted/50 transition-colors ${activePoi === poi ? 'bg-primary/5' : ''}`}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 shadow-sm" style={{ backgroundColor: poi.color }}>
                  <span className="text-white text-lg">{poi.icon}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{poi.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{POI_TYPES[poi.category]?.label}</p>
                </div>
                <div className="text-right shrink-0">
                  <div className="flex items-center gap-1.5 justify-end text-sm font-bold text-slate-700 dark:text-slate-300">
                    {poi.drivingDurationText} <Car className="w-3.5 h-3.5 text-muted-foreground" />
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{poi.distanceText}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

export default PropertyMapOptimized;
